import { env } from '@botto/core';
import { smsClient } from '../client';
import { mapDetailHistory, mapDetailsResponse } from '../mappers/detailsMap';
import { mapReportToStatsReportRows, extractReportTotal } from '../mappers/reportMap';
import type {
  DetailsQuery,
  MessageLogRow,
  PageResult,
  ReportGroupBy,
  StatsReportQuery,
  StatsReportResult,
} from '../types';
import { smsGet, smsGetBlob, triggerBrowserDownload } from './http';

type CleanQueryValue = string | number | boolean | null | undefined | Array<string | number | boolean>;

function cleanQuery(
  query: Record<string, CleanQueryValue>,
): Record<string, string | number | boolean | Array<string | number | boolean>> {
  const out: Record<string, string | number | boolean | Array<string | number | boolean>> = {};
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out[k] = v as string | number | boolean | Array<string | number | boolean>;
  }
  return out;
}

function reportQueryParams(query: StatsReportQuery, asJson: boolean) {
  const group_by = (query.group_by?.length ? query.group_by : ['created_day']) as ReportGroupBy[];
  return {
    group_by,
    ...(asJson ? { format: 'json' as const } : {}),
    report_currency: query.report_currency || undefined,
    client_id: query.client_id ?? undefined,
    manager_id: query.manager_id ?? undefined,
    partner_id: query.partner_id ?? undefined,
    direction_id: query.direction_id ?? undefined,
    country_iso: query.country_iso || undefined,
    aggregator_id: query.aggregator_id ?? undefined,
    date_from: query.date_from || undefined,
    date_to: query.date_to || undefined,
  };
}

/** Aggregated SMS stats from GET /report/?format=json. */
export async function fetchStatsReport(query: StatsReportQuery): Promise<StatsReportResult> {
  const params = reportQueryParams(query, true);
  const group_by = params.group_by;
  const { data, error } = await smsClient.GET('/report/', {
    params: { query: params },
  });
  if (error) throw error;
  return {
    items: mapReportToStatsReportRows(data, group_by),
    group_by,
    total: extractReportTotal(data),
  };
}

/** Message details from GET /details/ (smsGet — OpenAPI types lag live query params). */
export async function fetchMessageDetails(
  query: DetailsQuery,
): Promise<PageResult<MessageLogRow>> {
  const page_size = Math.min(query.page_size ?? env.defaultPageSize, 200);
  const offset =
    typeof query.offset === 'number'
      ? Math.max(0, query.offset)
      : Math.max(0, ((query.page ?? 1) - 1) * page_size);
  const page = page_size > 0 ? Math.floor(offset / page_size) + 1 : 1;
  const data = await smsGet<unknown>(
    '/details/',
    cleanQuery({
      offset,
      limit: page_size,
      date_from: query.date_from || undefined,
      date_to: query.date_to || undefined,
      status: query.status || undefined,
      phone_to: query.phone_to || undefined,
      phone_from: query.phone_from || undefined,
      user_id: query.user_id ?? undefined,
      system_id: query.system_id || undefined,
      message_id: query.message_id || undefined,
      correlation_id: query.correlation_id || undefined,
      stream_ids: query.stream_ids ?? undefined,
      manager_id: query.manager_id ?? undefined,
      partner_id: query.partner_id ?? undefined,
      direction_id: query.direction_id ?? undefined,
      country_iso: query.country_iso || undefined,
      operator: query.operator || undefined,
      aggregator_id: query.aggregator_id ?? undefined,
      message_type: query.message_type ?? undefined,
    }),
  );
  return mapDetailsResponse(data, { page, page_size });
}

/** Status history for one message: GET /details/{correlation_id}. */
export async function fetchMessageDetailByCorrelation(
  correlationId: string,
): Promise<MessageLogRow[]> {
  const id = correlationId.trim();
  if (!id) return [];
  const raw = await smsGet<unknown>(`/details/${encodeURIComponent(id)}`);
  return mapDetailHistory(raw);
}

/** Download full stats Excel for current filters (server /report/ xlsx). */
export async function exportStatsExcel(query: StatsReportQuery): Promise<void> {
  const params = reportQueryParams(query, false);
  const file = await smsGetBlob(
    '/report/',
    cleanQuery({
      ...params,
      group_by: params.group_by,
    }) as Record<string, string | number | boolean | undefined | null | Array<string | number | boolean>>,
  );
  triggerBrowserDownload(file.blob, file.filename || 'sms-stats.xlsx');
}
